import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import type { Fetcher, Navigation } from "react-router"
import { toast } from "react-toastify/unstyled"
import { MetadataValue } from "~/server/gen_client/unacast/boundaries/boundarypb/v1/boundary_pb"
import { AUTH_PATHS, INTENTS } from "./consts"
import { isSafeRelativePath } from "./authLinks"

// Only same-site paths are followed after login: an absolute or
// protocol-relative redirectTo would turn the login page into an open
// redirect (log in on the real site, land on a look-alike). Never an auth
// page either (loop guard), compared on the pathname so a query string can't
// defeat the check.
export function sanitizeRedirectTo(raw: string | null): string {
  const redirectTo = raw || "/"
  if (!isSafeRelativePath(redirectTo)) return "/"
  const pathname = redirectTo.split(/[?#]/)[0]
  return AUTH_PATHS.includes(pathname) ? "/" : redirectTo
}

export const isValidEmail = (email: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)

export const cn = (...inputs: ClassValue[]) => {
  return twMerge(clsx(inputs))
}

export const handleToast = (fetcher: Fetcher, navigation: Navigation) => {
  if (navigation.state === "idle") {
    switch (true) {
      case toast.isActive("create-collection"):
        updateToast(
          "create-collection",
          "Collection created successfully",
          "success",
        )
        break
      case toast.isActive("new-version"):
        updateToast(
          "new-version",
          "New version created successfully",
          "success",
        )
        break
      case toast.isActive("delete-draft"):
        updateToast("delete-draft", "Draft deleted successfully", "success")
        break
      default:
        break
    }
    if (fetcher.data?.status === 200) {
      switch (fetcher.data?.intent) {
        case INTENTS.ARCHIVE_VERSION:
          updateToast(
            "archive-version",
            "Version archived successfully",
            "success",
          )
          break
        case INTENTS.UNARCHIVE_VERSION:
          updateToast(
            "unarchive-version",
            "Version unarchived successfully",
            "success",
          )
          break
        default:
          break
      }
    }
  }
}

const updateToast = (
  toastId: string,
  message: string,
  variant: "success" | "error" | "default",
) => {
  toast.update(toastId, {
    render: message,
    type: variant,
    isLoading: false,
    autoClose: 3000,
  })
}

export const getDisplayValue = (metaValue?: MetadataValue): string => {
  if (!metaValue?.data?.case) {
    return ""
  }
  if (metaValue.data.case === "enforced") {
    const { displayName, typesetName } = metaValue.data.value
    return `${displayName} (${typesetName})`
  }
  return metaValue.data.value // for freeform
}
